import { useCallback, useEffect, useRef } from 'react';
import { useImage } from '@/store/image';

export interface UseImageEditorResult {
  image: HTMLImageElement | null;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  previewCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  // loadImage: (imageSource: File | string) => void;
}

export const useImageEditor = (): UseImageEditorResult => {
  const image = useImage.use.image();
  // const setImage = useImage.use.setImage();
  const isCropping = useImage.use.isCropping();
  // const setIsCropping = useImage.use.setIsCropping();
  const backgroundColor = useImage.use.backgroundColor();
  const rotation = useImage.use.rotation();
  const flipHorizontal = useImage.use.flipHorizontal();
  const flipVertical = useImage.use.flipVertical();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  // const loadImage = useCallback((imageSource: File | string) => {
  //   const img = new Image();
  //   img.crossOrigin = 'anonymous';

  //   img.onload = () => {
  //     setImage(img);
  //   };

  //   if (typeof imageSource === 'string') {
  //     img.src = imageSource;
  //   } else {
  //     const url = URL.createObjectURL(imageSource);
  //     img.src = url;
  //   }

  //   setIsCropping(true);
  // }, []);

  const drawImage = useCallback(() => {
    if (!image || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = image.width;
    canvas.height = image.height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (backgroundColor !== 'transparent') {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    ctx.save();

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    ctx.translate(centerX, centerY);
    ctx.rotate((rotation * Math.PI) / 180);

    if (flipHorizontal) {
      ctx.scale(-1, 1);
    }
    if (flipVertical) {
      ctx.scale(1, -1);
    }

    ctx.drawImage(image, -image.width / 2, -image.height / 2);
    ctx.restore();
  }, [image, backgroundColor, rotation, flipHorizontal, flipVertical]);

  const updatePreview = useCallback(() => {
    if (!image || !previewCanvasRef.current) return;

    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const maxSize = 300;
    const aspectRatio = image.width / image.height;
    let previewWidth = maxSize;
    let previewHeight = maxSize / aspectRatio;
    if (previewHeight > maxSize) {
      previewHeight = maxSize;
      previewWidth = maxSize * aspectRatio;
    }

    canvas.width = previewWidth;
    canvas.height = previewHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (backgroundColor !== 'transparent') {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    ctx.save();
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    ctx.translate(centerX, centerY);
    ctx.rotate((rotation * Math.PI) / 180);
    if (flipHorizontal) ctx.scale(-1, 1);
    if (flipVertical) ctx.scale(1, -1);
    ctx.drawImage(image, -previewWidth / 2, -previewHeight / 2);
    ctx.restore();
  }, [image, backgroundColor, rotation, flipHorizontal, flipVertical]);

  useEffect(() => {
    drawImage();
    updatePreview();
  }, [drawImage, updatePreview]);

  // Ensure canvas redraws when toggling crop mode off
  useEffect(() => {
    if (!isCropping) {
      drawImage();
      updatePreview();
    }
  }, [isCropping, drawImage, updatePreview]);

  return {
    image,
    canvasRef,
    // loadImage,
    previewCanvasRef,
  };
};

export default useImageEditor;
